<script setup lang="ts">
import type { RatioPresetId } from '@/constants/mockupDevices'
import { RATIO_PRESETS } from '@/constants/mockupDevices'

const props = defineProps<{
  modelValue: RatioPresetId | null
  groupLabel?: string
}>()

const emit = defineEmits<{
  (e: 'select', value: RatioPresetId): void
}>()

const items = RATIO_PRESETS as readonly RatioPresetId[]

function isActive(id: RatioPresetId): boolean {
  return props.modelValue === id
}
</script>

<template>
  <div class="grid" :aria-label="props.groupLabel ?? 'Aspect ratio presets'">
    <button
      v-for="id in items"
      :key="id"
      type="button"
      class="cell"
      :class="{ active: isActive(id) }"
      :aria-pressed="isActive(id) ? 'true' : 'false'"
      @click="emit('select', id)"
    >
      <span class="ico" aria-hidden="true">
        <span class="frame" :data-ratio="id" />
      </span>
      <span class="lbl">{{ id }}</span>
    </button>
  </div>
</template>

<style scoped>
.grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0.45rem;
}

@media (max-width: 340px) {
  .grid {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

.cell {
  border: 1px solid var(--im-preset-border);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--im-preset-bg) 92%, var(--im-surface-2)),
    var(--im-preset-bg)
  );
  border-radius: 10px;
  padding: 0.45rem 0.35rem 0.4rem;
  cursor: pointer;
  display: grid;
  justify-items: center;
  gap: 0.35rem;
  transition:
    border-color 160ms ease,
    background 160ms ease,
    transform 120ms ease;
}

.cell:hover {
  border-color: var(--im-preset-border-hover);
  background: var(--im-preset-hover-bg);
}

.cell:focus-visible {
  outline: 2px solid var(--im-accent);
  outline-offset: 2px;
}

.cell.active {
  border-color: var(--im-preset-active-border);
  background: linear-gradient(
    180deg,
    color-mix(in srgb, var(--im-preset-active-bg) 90%, var(--im-accent)),
    var(--im-preset-active-bg)
  );
  color: var(--im-preset-active-fg);
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--im-preset-active-border) 35%, transparent),
    0 6px 20px color-mix(in srgb, var(--im-accent) 18%, transparent);
}

.cell.active .lbl {
  color: var(--im-preset-active-fg);
}

.lbl {
  font-size: 0.6875rem;
  font-weight: 800;
  color: var(--im-text);
}

.ico {
  width: 34px;
  height: 34px;
  display: grid;
  place-items: center;
}

.frame {
  border: 1.5px solid currentColor;
  border-radius: 3px;
  opacity: 0.92;
  color: var(--im-preset-frame-muted);
}

.frame[data-ratio='1:1'] {
  width: 22px;
  height: 22px;
}
.frame[data-ratio='3:4'] {
  width: 16px;
  height: 22px;
}
.frame[data-ratio='4:3'] {
  width: 22px;
  height: 16px;
}
.frame[data-ratio='16:9'] {
  width: 26px;
  height: 15px;
}
.frame[data-ratio='9:16'] {
  width: 15px;
  height: 26px;
}
.frame[data-ratio='4:5'] {
  width: 16px;
  height: 20px;
}
.frame[data-ratio='21:9'] {
  width: 30px;
  height: 12px;
}

.cell.active .frame {
  border-color: var(--im-preset-active-fg);
  color: var(--im-preset-active-fg);
}

.cell:active:not(.active) {
  transform: translateY(1px);
}
</style>
